import { useEffect, useCallback } from 'react'

type KeyHandler = (e: KeyboardEvent) => void

interface KeyMapping {
  /** Keys that trigger this action (lowercase). Use 'space', 'enter', 'arrowleft', etc. for special keys. */
  keys: string[]
  /** Handler function. Return true to preventDefault. Async handlers supported. */
  handler: () => boolean | void | Promise<boolean | void>
  /** Only trigger when answer is shown (optional) */
  requiresShowBack?: boolean
  /** Whether to preventDefault for this key (default: false) */
  preventDefault?: boolean
}

interface UseStudyKeyboardOptions {
  /** Whether keyboard shortcuts are enabled */
  enabled: boolean
  /** Whether the answer/back side is currently shown */
  showBack: boolean
  /** Key mappings for this study mode */
  keyMappings: KeyMapping[]
  /** Additional condition to skip all key handling (e.g., dialog open) */
  skipWhen?: boolean
}

/**
 * Hook for handling keyboard shortcuts in study modes.
 *
 * Handles common boilerplate:
 * - Ignores keypresses when typing in inputs/textareas
 * - Manages event listener lifecycle
 * - Maps keys to actions with optional showBack requirement
 *
 * @example
 * useStudyKeyboard({
 *   enabled: sessionStarted,
 *   showBack,
 *   keyMappings: [
 *     { keys: [' '], handler: () => { setShowBack(true); return true } },
 *     { keys: ['a'], handler: () => handleCardAction('mastered'), requiresShowBack: true },
 *     { keys: ['s'], handler: () => handleCardAction('recalled'), requiresShowBack: true },
 *   ]
 * })
 */
export function useStudyKeyboard({
  enabled,
  showBack,
  keyMappings,
  skipWhen = false,
}: UseStudyKeyboardOptions) {
  const handleKeyPress: KeyHandler = useCallback(
    (e: KeyboardEvent) => {
      // Skip when explicitly disabled
      if (skipWhen) return

      // Prevent shortcuts when typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      // Normalize key names
      const key = e.key.toLowerCase()
      const normalizedKey = key === ' ' ? 'space' : key

      // Find matching mapping
      for (const mapping of keyMappings) {
        const keyMatches = mapping.keys.some(
          (k) => k.toLowerCase() === normalizedKey || k.toLowerCase() === key
        )

        if (keyMatches) {
          // Check showBack requirement
          if (mapping.requiresShowBack && !showBack) {
            continue
          }

          // preventDefault if explicitly requested
          if (mapping.preventDefault) {
            e.preventDefault()
          }

          // Call handler - may be async
          const result = mapping.handler()

          // Also support returning true to preventDefault (sync handlers)
          if (result === true) {
            e.preventDefault()
          }
          return
        }
      }
    },
    [showBack, keyMappings, skipWhen]
  )

  useEffect(() => {
    if (!enabled) return

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [enabled, handleKeyPress])
}

/**
 * Common key mappings for study modes.
 * These can be used as-is or combined with custom mappings.
 */
export const commonStudyKeys = {
  /** Show answer on space */
  showAnswer: (setShowBack: (show: boolean) => void, showBack: boolean): KeyMapping => ({
    keys: [' ', 'space'],
    handler: () => {
      if (!showBack) {
        setShowBack(true)
        return true
      }
    },
  }),

  /** Toggle translations on T */
  toggleTranslations: (
    showTranslations: boolean,
    setShowTranslations: (show: boolean) => void
  ): KeyMapping => ({
    keys: ['t'],
    handler: () => {
      setShowTranslations(!showTranslations)
    },
  }),

  /** Speak all TTS text on F */
  speakAll: (): KeyMapping => ({
    keys: ['f'],
    handler: () => {
      const buttons = document.querySelectorAll('button[onclick*="speakText"]')
      buttons.forEach((button) => (button as HTMLButtonElement).click())
    },
  }),

  /** Toggle audio playback on P */
  toggleAudio: (): KeyMapping => ({
    keys: ['p'],
    handler: () => {
      const toggleAudio = (window as any).toggleAudio
      if (typeof toggleAudio === 'function') {
        toggleAudio()
      }
    },
  }),
}
